import api from "./client";


// ========== CONFERÊNCIA DE NOTA FISCAL ==========

export interface ConferenciaVolumeItemResumo {
	conferencia_nota_volume_item_id: number;
	nota_fiscal_item_id: number | null;
	quantidade_esperada: number;
	quantidade_bipada: number;
	ean_bipado: string | null;
	// Preenchido pelo backend quando nota_fiscal_item_id é null:
	// descrição do produto buscada na tabela Produto via EAN (pode ser null se EAN
	// não estiver cadastrado).
	descricao_externa?: string | null;
	nota_fiscal_item?: {
		nota_fiscal_item_id: number;
		descricao_xml: string;
		ean: string | null;
		unidade: string;
		produto?: { produto_id: number; descricao: string; ean: string | null } | null;
	};
}

export interface ConferenciaVolumeResumo {
	conferencia_nota_volume_id: number;
	nota_fiscal_volume_id: number;
	numero_volume: number;
	ean: string;
	status: "A_CONFERIR" | "EM_ANDAMENTO" | "CONFERIDO";
	operador_id: number | null;
	operador?: { usuario_id: number; responsavel: string } | null;
	data_inicio: string | null;
	data_fim: string | null;
	tentativa_atual: number;
	// Estado final da comparação 1ª×2ª: true quando o volume divergiu entre a 1ª
	// e a 2ª conferência (usado para pintar o volume de vermelho em "Ver Volumes").
	divergiu_primeira_segunda?: boolean;
	reconferencia_forcada_feita?: boolean;
	itens?: ConferenciaVolumeItemResumo[];
}

export interface ConferenciaOperadorResumo {
	conferencia_nota_operador_id: number;
	operador_id: number;
	quantidade_volumes: number;
	volumes_conferidos: number;
	operador?: { usuario_id: number; responsavel: string } | null;
}

export type ModoBipagem = "UNITARIO" | "POR_QUANTIDADE";

export interface ConferenciaNotaResumo {
	conferencia_nota_id: number;
	nota_fiscal_id: number;
	tipo_conferencia: string;
	modo_bipagem: ModoBipagem;
	status: "EM_ANDAMENTO" | "CONCLUIDA" | "AGUARDANDO_RECONFERENCIA" | "RECONFERINDO";
	tentativa: number;
	tem_ocorrencia_pendente?: boolean;
	conferencia_atrasada?: boolean;
	iniciado_por_usuario?: { usuario_id: number; responsavel: string } | null;
	created_at?: string;
	// Contadores injetados pelo backend no findAll (loadRelationCountAndMap).
	volumes_total?: number;
	volumes_conferidos?: number;
	// #1302 — todos os operadores atribuídos à conferência (join no findAll).
	operadores?: ConferenciaOperadorResumo[];
	// #1302 — resolvidos pelo backend via grupo de vínculo.
	numeros_remessa?: string[];
	fornecedor_razao_social?: string | null;
	volumes: ConferenciaVolumeResumo[];
	nota_fiscal?: {
		nota_fiscal_id: number;
		numero_nota: string;
		serie: string;
		status?: string;
		nota_origem?: string | null;
		grupo_vinculo_id?: number | null;
		emitente?: { razao_social: string; cnpj: string };
		volumes?: { numero_volume: number; ean: string }[];
		itens?: {
			nota_fiscal_item_id: number;
			descricao_xml: string;
			ean: string | null;
			unidade: string;
			quantidade: number;
			produto?: { produto_id: number; descricao: string; ean: string | null } | null;
		}[];
	};
}

export interface ConferenciaNotaDetalhe extends ConferenciaNotaResumo {
	operadores: ConferenciaOperadorResumo[];
	ocorrencias: OcorrenciaNotaResumo[];
}

export interface ConferenciaHistoricoItem {
	conferencia_nota_historico_id: number;
	conferencia_nota_id: number;
	nota_fiscal_id: number;
	nota_fiscal_item_id: number | null;
	ean_bipado: string | null;
	conferencia_nota_volume_id: number | null;
	numero_volume: number | null;
	tentativa: number;
	quantidade_esperada: number;
	quantidade_bipada: number;
	operador_id: number | null;
	operador?: { usuario_id: number; responsavel: string } | null;
	/**
	 * #1598: janela em que o volume foi conferido NESTA tentativa. Null no
	 * histórico anterior à #1598 — o volume perdia as datas no reset e elas não
	 * eram copiadas para o snapshot.
	 */
	volume_data_inicio: string | null;
	volume_data_fim: string | null;
	/**
	 * Instante do SNAPSHOT (fim da rodada), não do bip: é `now()` da transação
	 * que grava a tentativa inteira, então vale igual em todas as linhas dela.
	 */
	data_registro: string;
	nota_fiscal_item?: {
		descricao_xml: string;
		ean: string | null;
		unidade: string;
		produto?: { descricao: string; ean: string | null } | null;
	};
}


export async function listarConferenciasNota(params?: {
	search?: string;
	status?: string;
	conferencia_atrasada?: string;
	com_ocorrencia_pendente?: string;
	fornecedor_cnpj?: string;
	dataInicio?: string;
	dataFim?: string;
	offset?: number;
	limit?: number;
}): Promise<{
	results: ConferenciaNotaResumo[];
	total: number;
	/** #1963: conferências por status, com os filtros menos o status. */
	por_status?: Record<string, number>;
}> {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.conferencia_atrasada) queryParams.conferencia_atrasada = params.conferencia_atrasada;
		if (params?.com_ocorrencia_pendente) queryParams.com_ocorrencia_pendente = params.com_ocorrencia_pendente;
		if (params?.fornecedor_cnpj) queryParams.fornecedor_cnpj = params.fornecedor_cnpj;
		if (params?.dataInicio) queryParams.dataInicio = params.dataInicio;
		if (params?.dataFim) queryParams.dataFim = params.dataFim;
		queryParams.offset = params?.offset ?? 0;
		queryParams.limit = params?.limit ?? 50;
		const response = await api.get("/conferencia-nota", { params: queryParams });
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar conferências.");
	}
}


export async function gerarConferenciaNota(dto: {
	nota_fiscal_id: number;
	operadores: { operador_id: number; quantidade_volumes: number }[];
	volumes_extras?: number;
	modo_bipagem?: ModoBipagem;
}): Promise<ConferenciaNotaDetalhe> {
	try {
		const response = await api.post("/conferencia-nota", dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao gerar conferência.");
	}
}


export async function buscarConferenciaNota(id: number): Promise<ConferenciaNotaDetalhe> {
	try {
		const response = await api.get(`/conferencia-nota/${id}`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar conferência.");
	}
}


export async function buscarConferenciaNotaPorNota(
	notaFiscalId: number,
): Promise<ConferenciaNotaDetalhe> {
	try {
		const response = await api.get(`/conferencia-nota/nota/${notaFiscalId}`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar conferência da nota.");
	}
}


export async function buscarHistoricoConferencia(
	conferenciaId: number,
): Promise<ConferenciaHistoricoItem[]> {
	try {
		const response = await api.get(`/conferencia-nota/${conferenciaId}/historico`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar histórico da conferência.");
	}
}


export async function realocarVolumesConferencia(
	conferenciaId: number,
	dto: { operadores: { operador_id: number; quantidade_volumes: number }[] },
): Promise<ConferenciaNotaDetalhe> {
	try {
		const response = await api.patch(`/conferencia-nota/${conferenciaId}/realocar`, dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao realocar volumes.");
	}
}


export async function resetarConferenciaManual(
	conferenciaId: number,
): Promise<void> {
	try {
		await api.patch(`/conferencia-nota/${conferenciaId}/reset-manual`);
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao resetar conferência.");
	}
}


export async function resetarTentativasConferencia(
	conferenciaId: number,
	ateTentativa: number,
): Promise<void> {
	try {
		await api.post(`/conferencia-nota/${conferenciaId}/resetar-tentativas`, {
			ate_tentativa: ateTentativa,
		});
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao resetar tentativa(s) da conferência.");
	}
}


export async function realocarVolumeReconferencia(
	volumeId: number,
	operadorId: number,
): Promise<ConferenciaVolumeResumo> {
	try {
		const response = await api.patch(`/conferencia-nota/volume/${volumeId}/realocar-reconferencia`, {
			operador_id: operadorId,
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao realocar volume.");
	}
}


// ========== RECONFERÊNCIA MANUAL ==========

export interface VolumeReconferenciaItem {
	descricao: string;
	ean: string;
	quantidade_bipada: number;
}

export interface VolumeReconferencia {
	conferencia_nota_volume_id: number;
	numero_volume: number;
	status: string;
	total_bipado: number;
	divergente: boolean;
	// true quando o volume divergiu entre 1ª e 2ª conferência — vem primeiro na
	// lista e recebe a tag "(conferência divergente)".
	divergiu_primeira_segunda: boolean;
	itens: VolumeReconferenciaItem[];
}

export interface VolumesReconferenciaResponse {
	conferencia_nota_id: number;
	tentativa: number;
	volumes: VolumeReconferencia[];
}

export async function buscarVolumesParaReconferencia(
	conferenciaId: number,
): Promise<VolumesReconferenciaResponse> {
	try {
		const response = await api.get(`/conferencia-nota/${conferenciaId}/volumes-reconferencia`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar volumes para reconferência.");
	}
}

export async function iniciarReconferenciaManual(
	conferenciaId: number,
	volumeIds: number[],
): Promise<ConferenciaNotaDetalhe> {
	try {
		const response = await api.post(`/conferencia-nota/${conferenciaId}/reconferencia-manual`, {
			volume_ids: volumeIds,
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao iniciar reconferência manual.");
	}
}


// ========== CONFERÊNCIA WEB — BIPAGEM OPERACIONAL ==========
// Wrappers consumidos pela tela operacional de bipagem na web. Espelham o
// contrato usado pelo mobile (conferencia-compra-notas.jsx). O backend resolve
// o operador pelo JWT — nenhum endpoint recebe operador_id.

export interface ValidarEanResponse {
	na_nota: boolean;
	quantidade_esperada: number;
	quantidade_bipada_outros: number;
	descricao: string | null;
}

export interface VolumesOperadorResponse {
	volumes_atribuidos: number[];
}

export async function buscarVolumesConferencia(
	conferenciaId: number,
): Promise<ConferenciaVolumeResumo[]> {
	try {
		const response = await api.get(`/conferencia-nota/${conferenciaId}/volumes`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar volumes da conferência.");
	}
}

export async function buscarVolumesOperador(
	conferenciaId: number,
): Promise<VolumesOperadorResponse> {
	try {
		const response = await api.get(
			`/conferencia-nota/${conferenciaId}/volumes-operador`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar volumes do operador.");
	}
}

export async function vincularVolume(
	volumeId: number,
): Promise<ConferenciaVolumeResumo> {
	try {
		const response = await api.patch(
			`/conferencia-nota/volume/${volumeId}/vincular`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao vincular volume.");
	}
}

export async function validarEanConferencia(
	conferenciaId: number,
	ean: string,
): Promise<ValidarEanResponse> {
	try {
		const response = await api.post(
			`/conferencia-nota/${conferenciaId}/validar-ean`,
			{ ean },
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao validar EAN.");
	}
}

export async function biparQuantidadeConferencia(
	conferenciaId: number,
	dto: { conferencia_nota_volume_id: number; ean: string; quantidade: number },
): Promise<ConferenciaVolumeItemResumo> {
	try {
		const response = await api.post(
			`/conferencia-nota/${conferenciaId}/bip-quantidade`,
			dto,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao registrar quantidade.");
	}
}

// O backend decrementa quantidade_bipada em 1 (semântica "−1", não delete) e
// devolve o item atualizado. Quem chama decide remover a linha quando chega a 0.
export async function removerBipConferencia(
	itemId: number,
): Promise<ConferenciaVolumeItemResumo> {
	try {
		const response = await api.delete(`/conferencia-nota/bip/${itemId}`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao remover item bipado.");
	}
}

// Resposta do finalizar volume: a entidade do volume acrescida de sinalização
// de reconferência forçada quando a 2ª conferência não bateu com a 1ª.
export interface FinalizarVolumeResponse extends ConferenciaVolumeResumo {
	reconferencia_forcada_requerida?: boolean;
	mensagem_reconferencia?: string;
}

export async function finalizarVolumeConferencia(
	volumeId: number,
	bips?: { ean: string; excedente?: boolean; quantidade?: number }[],
): Promise<FinalizarVolumeResponse> {
	try {
		const response = await api.patch(`/conferencia-nota/volume/${volumeId}/finalizar`, {
			bips: bips ?? [],
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao finalizar volume.");
	}
}


// ========== OCORRÊNCIAS DE NOTA FISCAL ==========

export interface OcorrenciaNotaItemImposto {
	tipo_imposto: string;
	base_calculo: number | null;
	aliquota: number | null;
	valor: number | null;
	cst: string | null;
}

export interface OcorrenciaNotaItemDetalhe {
	ocorrencia_nota_item_id: number;
	nota_fiscal_item_id: number | null;
	ean_bipado: string | null;
	quantidade_esperada: number | null;
	quantidade_encontrada: number | null;
	observacao: string | null;
	tratativa: string | null;
	tratativa_tipo: string | null;
	tratativa_concluida: boolean;
	devolucao_cfop: string | null;
	devolucao_observacao: string | null;
	devolucao_status: "PENDENTE" | "RASCUNHO_SALVO" | "RASCUNHO_TINY" | "NOTA_EMITIDA" | null;
	nota_fiscal_item?: {
		nota_fiscal_item_id: number;
		descricao_xml: string;
		codigo_produto_xml?: string | null;
		ean: string | null;
		unidade: string;
		ncm: string | null;
		valor_unitario: number;
		valor_total: number;
		quantidade: number;
		produto?: { produto_id: number; descricao: string } | null;
		cfop_item?: { cfop_id: number; codigo: string; natureza_operacao: string } | null;
		impostos?: OcorrenciaNotaItemImposto[];
	};
	produto_lookup?: { produto_id: number; descricao: string; ean: string | null } | null;
}

export interface OcorrenciaNotaResumo {
	ocorrencia_nota_id: number;
	nota_fiscal_id: number;
	conferencia_nota_id: number | null;
	tipo: string;
	status: "ABERTA" | "EM_ANALISE" | "RESOLVIDA" | "ACEITA" | "REJEITADA" | "PENDENTE_DEVOLUCAO";
	descricao: string | null;
	acao_tomada: string | null;
	data_criacao: string;
	data_resolucao: string | null;
	data_assumida: string | null;
	nota_complementar_id: number | null;
	nota_complementar?: { nota_fiscal_id: number; numero_nota: string } | null;
	devolucao_pendente: boolean;
	data_devolucao_emitida: string | null;
	devolucao_observacao: string | null;
	devolucao_cfop: string | null;
	devolucao_finalidade: string | null;
	id_nota_devolucao_tiny: number | null;
	numero_nota_devolucao: number | null;
	serie_nota_devolucao: string | null;
	chave_acesso_devolucao: string | null;
	xml_devolucao: string | null;
	criado_por_usuario?: { usuario_id: number; responsavel: string } | null;
	assumido_por_usuario?: { usuario_id: number; responsavel: string } | null;
	resolvido_por_usuario?: { usuario_id: number; responsavel: string } | null;
	itens: OcorrenciaNotaItemDetalhe[];
	nota_fiscal?: {
		numero_nota: string;
		chave_acesso: string;
		emitente_cnpj: string;
		cfop?: { codigo: string; natureza_operacao: string } | null;
		emitente?: { razao_social: string; cnpj: string; uf: string } | null;
	};
}


export async function listarOcorrenciasNota(params?: {
	search?: string;
	status?: string;
	tipo?: string;
	nota_fiscal_id?: number;
	offset?: number;
	limit?: number;
}): Promise<{ results: OcorrenciaNotaResumo[]; total: number }> {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.tipo) queryParams.tipo = params.tipo;
		if (params?.nota_fiscal_id) queryParams.nota_fiscal_id = params.nota_fiscal_id;
		queryParams.offset = params?.offset ?? 0;
		queryParams.limit = params?.limit ?? 50;
		const response = await api.get("/ocorrencia-nota", { params: queryParams });
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar ocorrências.");
	}
}


export async function assumirOcorrenciaNota(
	id: number,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(`/ocorrencia-nota/${id}/assumir`, {});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao assumir ocorrência.");
	}
}


export async function resolverOcorrenciaNota(
	id: number,
	acaoTomada: string,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(`/ocorrencia-nota/${id}/resolver`, {
			acao_tomada: acaoTomada,
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao resolver ocorrência.");
	}
}


export async function vincularProdutoItemNF(
	notaFiscalItemId: number,
	produtoId: number,
): Promise<void> {
	try {
		await api.patch(`/nota-fiscal/item/${notaFiscalItemId}/vincular-produto`, {
			produto_id: produtoId,
		});
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao vincular produto.");
	}
}

export interface ComponenteKit {
	produto_id: number;
	quantidade: number;
}

/** O que a sugestão devolve: o componente já com nome, para a tela montar a lista. */
export interface ComponenteKitSugerido extends ComponenteKit {
	descricao: string;
	sku: string;
}

/**
 * #1980 — item de nota que é kit: um código do fornecedor, vários produtos.
 *
 * Endpoint é o mesmo do vínculo simples; o que muda é o corpo. Backend recusa
 * os dois campos juntos, então são funções separadas aqui também.
 */
export async function vincularComponentesKitNF(
	notaFiscalItemId: number,
	componentes: ComponenteKit[],
): Promise<void> {
	try {
		await api.patch(`/nota-fiscal/item/${notaFiscalItemId}/vincular-produto`, {
			componentes,
		});
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao vincular o kit.");
	}
}

/** Componentes já usados para este mesmo código do fornecedor. Vazio é normal. */
export async function sugerirComponentesKitNF(
	notaFiscalItemId: number,
): Promise<ComponenteKitSugerido[]> {
	try {
		const { data } = await api.get(
			`/nota-fiscal/item/${notaFiscalItemId}/componentes-sugeridos`,
		);
		return Array.isArray(data) ? data : [];
	} catch {
		// Sugestão é conveniência: falhar aqui não pode impedir o vínculo manual.
		return [];
	}
}


export async function definirTratativasOcorrencia(
	id: number,
	itens: Array<{
		ocorrencia_nota_item_id: number;
		tratativa: string;
		tratativa_tipo: string;
		devolucao_cfop?: string;
		devolucao_observacao?: string;
	}>,
	notaFields?: { devolucao_cfop?: string; devolucao_finalidade?: string },
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(`/ocorrencia-nota/${id}/definir-tratativas`, {
			itens,
			...notaFields,
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao definir tratativas.");
	}
}


export async function concluirOcorrenciaNota(
	id: number,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(`/ocorrencia-nota/${id}/concluir`, {});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao concluir ocorrência.");
	}
}


export async function vincularNotaComplementar(
	ocorrenciaId: number,
	notaFiscalId: number,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(
			`/ocorrencia-nota/${ocorrenciaId}/vincular-complementar`,
			{ nota_fiscal_id: notaFiscalId },
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao vincular nota complementar.");
	}
}


export async function salvarRascunhoDevolucao(
	itemId: number,
	body: { devolucao_cfop?: string; devolucao_observacao?: string },
): Promise<OcorrenciaNotaItemDetalhe> {
	try {
		const response = await api.patch(
			`/ocorrencia-nota/item/${itemId}/salvar-rascunho`,
			body,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao salvar rascunho.");
	}
}


// Issue #945 — salva rascunho no nível da NOTA (CFOP + finalidade únicos).
export async function salvarRascunhoDevolucaoNota(
	ocorrenciaId: number,
	body: {
		devolucao_cfop?: string;
		devolucao_finalidade?: string;
		devolucao_observacao?: string;
	},
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(
			`/ocorrencia-nota/${ocorrenciaId}/salvar-rascunho-devolucao`,
			body,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao salvar rascunho.");
	}
}


export async function criarRascunhoTiny(
	ocorrenciaId: number,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(
			`/ocorrencia-nota/${ocorrenciaId}/criar-rascunho-tiny`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao criar rascunho no Tiny.");
	}
}


export async function emitirNotaTiny(
	ocorrenciaId: number,
): Promise<OcorrenciaNotaResumo> {
	try {
		const response = await api.patch(
			`/ocorrencia-nota/${ocorrenciaId}/emitir-nota-tiny`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao emitir nota no Tiny.");
	}
}


export async function obterXmlDevolucao(
	ocorrenciaId: number,
): Promise<{ xml: string; chave_acesso: string; numero: number | null }> {
	try {
		const response = await api.get(
			`/ocorrencia-nota/${ocorrenciaId}/xml-devolucao`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao obter XML da devolução.");
	}
}


// ========== SEPARAÇÃO DE OCORRÊNCIA ==========

export interface SeparacaoOcorrenciaVolumeItemResumo {
	separacao_ocorrencia_volume_item_id: number;
	nota_fiscal_item_id: number;
	ocorrencia_nota_item_id: number | null;
	ean: string | null;
	quantidade_a_remover: number;
	quantidade_removida: number;
	removido_por_operador_id?: number | null;
	nota_fiscal_item?: {
		nota_fiscal_item_id: number;
		descricao_xml: string;
		ean: string | null;
		unidade: string;
		produto?: { produto_id: number; descricao: string; ean: string | null } | null;
	};
}

export interface SeparacaoOcorrenciaVolumeResumo {
	separacao_ocorrencia_volume_id: number;
	conferencia_nota_volume_id: number;
	numero_volume: number;
	operador_id: number | null;
	operador?: { usuario_id: number; responsavel: string } | null;
	status: "A_SEPARAR" | "EM_ANDAMENTO" | "SEPARADO";
	ordem: number;
	data_inicio: string | null;
	data_fim: string | null;
	itens: SeparacaoOcorrenciaVolumeItemResumo[];
}

export interface SeparacaoOcorrenciaResumo {
	separacao_ocorrencia_id: number;
	conferencia_nota_id: number;
	nota_fiscal_id: number;
	operador_id: number | null;
	operador?: { usuario_id: number; responsavel: string } | null;
	status: "PENDENTE" | "EM_ANDAMENTO" | "CONCLUIDA" | "CANCELADA";
	data_criacao: string;
	data_inicio: string | null;
	data_conclusao: string | null;
	volumes: SeparacaoOcorrenciaVolumeResumo[];
	nota_fiscal?: {
		nota_fiscal_id: number;
		numero_nota: string;
		serie: string;
		emitente?: { razao_social: string; cnpj: string };
	};
}


export async function listarSeparacoesOcorrencia(params?: {
	search?: string;
	status?: string;
	conferencia_nota_id?: number;
	offset?: number;
	limit?: number;
}): Promise<{ results: SeparacaoOcorrenciaResumo[]; total: number }> {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.conferencia_nota_id) queryParams.conferencia_nota_id = params.conferencia_nota_id;
		queryParams.offset = params?.offset ?? 0;
		queryParams.limit = params?.limit ?? 50;
		const response = await api.get("/separacao-ocorrencia", { params: queryParams });
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar separações de ocorrência.");
	}
}


export async function buscarSeparacaoOcorrencia(
	id: number,
): Promise<SeparacaoOcorrenciaResumo> {
	try {
		const response = await api.get(`/separacao-ocorrencia/${id}`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar separação de ocorrência.");
	}
}


export async function criarSeparacaoOcorrencia(
	dto: { conferencia_nota_id: number; operador_ids: number[] },
): Promise<SeparacaoOcorrenciaResumo> {
	try {
		const response = await api.post("/separacao-ocorrencia", dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao criar separação de ocorrência.");
	}
}

export async function realocarVolumesSeparacao(
	separacaoId: number,
	dto: { operador_id: number; volume_ids: number[] },
): Promise<SeparacaoOcorrenciaResumo> {
	try {
		const response = await api.patch(`/separacao-ocorrencia/${separacaoId}/realocar`, dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao realocar volumes de separação.");
	}
}


// ========== ENTRADA DE ESTOQUE ==========

export interface EntradaEstoqueVolumeResumo {
	entrada_estoque_volume_id: number;
	conferencia_nota_volume_id: number;
	numero_volume: number;
	ordem: number;
	localizacao_id: number | null;
	localizacao?: { localizacao_id: number; nome: string; ean: string } | null;
	status: "PENDENTE" | "EM_ANDAMENTO" | "CONCLUIDO";
	data_inicio: string | null;
	data_fim: string | null;
	concluido_por_operador_id?: number | null;
	// Quem reivindicou o volume enquanto está EM_ANDAMENTO (claim). Liberado na
	// conclusão — o crédito fica em concluido_por_operador_id.
	em_andamento_por_operador_id?: number | null;
	conferencia_nota_volume?: ConferenciaVolumeResumo;
}

export interface EntradaEstoqueOperadorResumo {
	entrada_estoque_operador_id: number;
	entrada_estoque_id: number;
	operador_id: number;
	operador?: { usuario_id: number; responsavel: string } | null;
}

export interface EntradaEstoqueItemResumo {
	entrada_estoque_item_id: number;
	entrada_estoque_id: number;
	nota_fiscal_item_id: number;
	produto_id: number;
	localizacao_id: number;
	conferencia_nota_volume_id: number | null;
	quantidade: number;
	status: "PENDENTE" | "EM_ANDAMENTO" | "CONCLUIDO";
	data_conclusao: string | null;
	concluido_por_operador_id?: number | null;
	localizacao?: { localizacao_id: number; nome: string; ean: string } | null;
	produto?: { produto_id: number; descricao: string; ean?: string };
	nota_fiscal_item?: { nota_fiscal_item_id: number; ean?: string; descricao_xml?: string };
}

// Método de alocação da entrada de estoque (espelha ModoAlocacaoEntrada do backend).
export type ModoEntradaEstoque =
	| "VOLUME"
	| "VOLUME_DESBLOQUEADO"
	| "POR_ITEM"
	| "POR_ITEM_DESBLOQUEADO";

/** #1656 — estado do lançamento de estoque da nota na Tiny. */
export type StatusLancamentoTiny =
	| "nao_iniciado"
	| "processando"
	| "pendente"
	| "bloqueado"
	| "incerto"
	| "concluido";

export interface EntradaEstoqueResumo {
	entrada_estoque_id: number;
	conferencia_nota_id: number;
	nota_fiscal_id: number;
	// Operador PRINCIPAL (primeiro atribuído). A lista completa vem em
	// `operadores` — a entrada pode ter N pessoas lançando em paralelo.
	operador_id: number | null;
	operador?: { usuario_id: number; responsavel: string } | null;
	operadores?: EntradaEstoqueOperadorResumo[];
	status: "PENDENTE" | "EM_ANDAMENTO" | "CONCLUIDA" | "CANCELADA";
	modo: ModoEntradaEstoque;
	data_criacao: string;
	data_inicio: string | null;
	data_conclusao: string | null;
	volumes: EntradaEstoqueVolumeResumo[];
	itens?: EntradaEstoqueItemResumo[];
	localizacoes?: { entrada_estoque_id: number; localizacao_id: number; localizacao: any }[];
	// #1656 — vêm da própria entidade, sem mudança no findOne/findAll do backend.
	tiny_lancamento_status?: StatusLancamentoTiny;
	tiny_lancamento_motivo?: string | null;
	tiny_lancamento_erro?: string | null;
	tiny_lancamento_tentativas?: number;
	tiny_lancamento_em?: string | null;
	nota_fiscal?: {
		nota_fiscal_id: number;
		numero_nota: string;
		serie: string;
		emitente?: { razao_social: string; cnpj: string };
	};
}

export interface ArmazemOption {
	armazem_id: number;
	nome: string;
	modo_entrada_estoque?: 'SEQUENCIAL' | 'FLEXIVEL';
}

export interface RuaInfo {
	rua: string;
	ultima_localizacao: number;
}

export async function listarEntradasEstoque(params?: {
	search?: string;
	status?: string;
	conferencia_nota_id?: number;
	offset?: number;
	limit?: number;
}): Promise<{ results: EntradaEstoqueResumo[]; total: number }> {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.conferencia_nota_id) queryParams.conferencia_nota_id = params.conferencia_nota_id;
		queryParams.offset = params?.offset ?? 0;
		queryParams.limit = params?.limit ?? 50;
		const response = await api.get("/entrada-estoque", { params: queryParams });
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar entradas de estoque.");
	}
}

export async function criarEntradaEstoque(dto: {
	conferencia_nota_id: number;
	// Todos os atribuídos veem a nota no mobile e pegam os volumes livres.
	operador_ids: number[];
	// VOLUME (padrão), VOLUME_DESBLOQUEADO ou POR_ITEM_DESBLOQUEADO (sem pool —
	// localização definida no mobile). POR_ITEM tem endpoint próprio.
	modo?: "VOLUME" | "VOLUME_DESBLOQUEADO" | "POR_ITEM_DESBLOQUEADO";
	localizacoes?: Array<{ armazem_id: number; rua: string; quantidade: number }>;
	localizacoes_lote?: Array<{ armazem_id: number; prefixo: string; de: number; ate: number; pad: number }>;
	localizacao_ids_existentes?: number[];
}): Promise<EntradaEstoqueResumo> {
	try {
		const response = await api.post("/entrada-estoque", dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao criar entrada de estoque.");
	}
}

export async function buscarEntradaEstoque(id: number): Promise<EntradaEstoqueResumo> {
	try {
		const response = await api.get(`/entrada-estoque/${id}`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar entrada de estoque.");
	}
}

export async function listarArmazensEntrada(): Promise<ArmazemOption[]> {
	try {
		const response = await api.get("/entrada-estoque/armazens");
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar armazéns.");
	}
}

export async function listarRuasArmazem(armazemId: number): Promise<RuaInfo[]> {
	try {
		const response = await api.get(`/entrada-estoque/armazem/${armazemId}/ruas`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao listar ruas do armazém.");
	}
}

export async function buscarVolumesDisponiveisEntrada(
	conferenciaNotaId: number,
): Promise<{ total_volumes: number; volumes_com_produtos: number }> {
	try {
		const response = await api.get(`/entrada-estoque/conferencia/${conferenciaNotaId}/volumes-disponiveis`);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar volumes disponíveis.");
	}
}


/**
 * Substitui a lista de operadores atribuídos à entrada — serve tanto para
 * adicionar quanto para remover.
 */
export async function definirOperadoresEntradaEstoque(
	entradaId: number,
	dto: { operador_ids: number[] },
): Promise<EntradaEstoqueResumo> {
	try {
		const response = await api.patch(`/entrada-estoque/${entradaId}/operadores`, dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao alterar os operadores da entrada de estoque.");
	}
}

export async function lancarEstoqueTiny(
	entradaId: number,
	opts?: { confirmarIncerto?: boolean },
): Promise<{ mensagem: string }> {
	try {
		const response = await api.post(`/entrada-estoque/${entradaId}/lancar-tiny`, {
			// Só vai no corpo quando alguém confirmou que olhou o estoque na Tiny.
			confirmar_incerto: opts?.confirmarIncerto ?? false,
		});
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao lançar estoque na Tiny.");
	}
}

/** #1656 — nota cuja entrada concluiu mas cujo estoque não subiu na Tiny. */
export interface PendenciaLancamentoTiny {
	entrada_estoque_id: number;
	nota_fiscal_id: number;
	numero_nota: string | null;
	emitente: string | null;
	status: StatusLancamentoTiny;
	motivo: string | null;
	motivo_texto: string;
	erro: string | null;
	tentativas: number;
	tiny_lancamento_em: string | null;
}

export async function listarPendenciasTiny(): Promise<{
	total: number;
	itens: PendenciaLancamentoTiny[];
}> {
	try {
		const response = await api.get("/entrada-estoque/pendencias-tiny");
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message) throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar as pendências de lançamento na Tiny.");
	}
}

export async function criarProdutoSimples(dados: {
	sku?: string;
	descricao: string;
	ean: string;
	marca_id?: number;
	categoria_id?: number;
}): Promise<any> {
	// Wrapper para criarProduto com campos obrigatórios preenchidos com defaults
	const { criarProduto } = await import('./estoque');
	return criarProduto({
		id_tiny: 0, // Default: não sincronizado com Tiny
		sku: dados.sku || dados.descricao.substring(0, 60),
		descricao: dados.descricao,
		ean: dados.ean,
		altura: 0,
		largura: 0,
		comprimento: 0,
		url_foto: null,
		marca_id: dados.marca_id,
		categoria_id: dados.categoria_id,
	});
}


// ========== ENTRADA DE ESTOQUE — MODO POR_ITEM ==========

export interface ItemDisponivelEntrada {
	nota_fiscal_item_id: number;
	produto_id: number;
	produto_nome: string;
	produto_ean: string;
	quantidade_disponivel: number;
}

export interface AlocacaoItemDto {
	nota_fiscal_item_id: number;
	/** #2069: qual produto do item vai nesta localização (kit tem vários). */
	produto_id?: number;
	quantidade: number;
	localizacao_id: number;
}

export interface VolumeDetalhe {
	numero_volume: number;
	quantidade: number;
}

export interface LocalizacaoPorItemResumo {
	localizacao_id: number;
	localizacao_nome: string;
	localizacao_ean: string;
	total_produtos: number;
	total_quantidade: number;
	concluido: boolean;
	itens: Array<{
		produto_id: number;
		produto_nome: string;
		produto_ean: string;
		quantidade_total: number;
		volumes: VolumeDetalhe[];
		status: string;
		entrada_estoque_item_ids: number[];
	}>;
}

export interface EntradaPorItemResponse {
	entrada_estoque_id: number;
	modo: string;
	status: string;
	localizacoes: LocalizacaoPorItemResumo[];
}

export async function buscarItensDisponiveisEntrada(
	conferenciaNotaId: number,
): Promise<ItemDisponivelEntrada[]> {
	try {
		const response = await api.get(
			`/entrada-estoque/conferencia/${conferenciaNotaId}/itens-disponiveis`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message)
			throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar itens disponíveis.");
	}
}

export async function criarEntradaEstoquePorItem(dto: {
	conferencia_nota_id: number;
	operador_ids: number[];
	alocacoes: AlocacaoItemDto[];
}): Promise<EntradaEstoqueResumo> {
	try {
		const response = await api.post("/entrada-estoque/por-item", dto);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message)
			throw new Error(error.response.data.message);
		throw new Error("Falha ao criar entrada de estoque por item.");
	}
}

export async function buscarLocalizacoesPorItem(
	entradaId: number,
): Promise<EntradaPorItemResponse> {
	try {
		const response = await api.get(
			`/entrada-estoque/${entradaId}/localizacoes-por-item`,
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message)
			throw new Error(error.response.data.message);
		throw new Error("Falha ao buscar localizações por item.");
	}
}

export async function biparLocalizacaoPorItem(
	entradaId: number,
	ean: string,
): Promise<EntradaPorItemResponse> {
	try {
		const response = await api.post(
			`/entrada-estoque/${entradaId}/bipar-localizacao-por-item`,
			{ ean },
		);
		return response.data;
	} catch (error: any) {
		if (error.response?.data?.message)
			throw new Error(error.response.data.message);
		throw new Error("Falha ao bipar localização.");
	}
}
